<template>
  <el-dialog
    title="Carte des parcelles"
    :visible.sync="visible"
    width="80%"
    :before-close="handleClose"
    append-to-body
  >
    <div class="map-container">
      <!-- Statistiques des parcelles -->
      <div class="map-stats">
        <div style="display: flex; gap: 10px;">
          <el-tag type="primary" size="small">
            <i class="el-icon-location" />
            {{ parcelles.length }} parcelle(s) totale(s)
          </el-tag>
          <el-tag type="success" size="small">
            <i class="el-icon-map-marker" />
            {{ parcellesAvecCoordonnees }} parcelle(s) avec coordonnées
          </el-tag>
        </div>
        <!-- Sélecteur de parcelle -->
        <div style="display: flex; align-items: center; gap: 10px;">
          <label for="">Parcelles: </label>
          <el-select
            v-model="selectedParcelle"
            placeholder="Sélectionner une parcelle"
            size="small"
            style="width: 200px;"
            @change="zoomToParcelle"
          >
            <el-option
              v-for="parcelle in parcellesAvecCoordonneesList"
              :key="parcelle.id"
              :label="parcelle.code"
              :value="parcelle.id"
            />
          </el-select>
        </div>
      </div>
      <div id="map" ref="mapContainer" class="map" />
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Fermer</el-button>
    </div>
  </el-dialog>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import customMarker from '@/assets/marquer.png';

export default {
  name: 'ParcelleMap',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    parcelles: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      map: null,
      mapLoaded: false,
      selectedParcelle: null,
      accessToken: 'pk.eyJ1IjoiZGV2b25lc295IiwiYSI6ImNsMTR0c2Y0MDBkdWIzZHF6cjk5OWJ1NnMifQ.cEu2E93WCC2rY74DEjqixg',
    };
  },

  computed: {
    parcellesAvecCoordonnees() {
      return this.parcelles.filter(parcelle => {
        if (!parcelle.gps) {
          return false;
        }
        let gpsData;
        try {
          gpsData = typeof parcelle.gps === 'string' ? JSON.parse(parcelle.gps) : parcelle.gps;
        } catch (error) {
          return false;
        }

        // Support array [lng, lat]
        if (Array.isArray(gpsData) && gpsData.length > 0) {
          return true;
        }

        // Support object {lat, lng} or {latitude, longitude}
        if (typeof gpsData === 'object' && gpsData !== null) {
          return (gpsData.lat !== undefined && gpsData.lng !== undefined) ||
                 (gpsData.latitude !== undefined && gpsData.longitude !== undefined);
        }

        return false;
      }).length;
    },
    parcellesAvecCoordonneesList() {
      return this.parcelles.filter(parcelle => {
        if (!parcelle.gps) {
          return false;
        }
        let gpsData;
        try {
          gpsData = typeof parcelle.gps === 'string' ? JSON.parse(parcelle.gps) : parcelle.gps;
        } catch (error) {
          return false;
        }

        // Support array [lng, lat]
        if (Array.isArray(gpsData) && gpsData.length > 0) {
          return true;
        }

        // Support object {lat, lng} or {latitude, longitude}
        if (typeof gpsData === 'object' && gpsData !== null) {
          return (gpsData.lat !== undefined && gpsData.lng !== undefined) ||
                 (gpsData.latitude !== undefined && gpsData.longitude !== undefined);
        }

        return false;
      });
    },
  },

  watch: {
    visible(newVal) {
      if (newVal && this.parcelles.length > 0) {
        this.$nextTick(() => {
          this.initMap();
        });
      } else if (!newVal) {
        // Nettoyer la carte quand le modal se ferme
        this.cleanupMap();
      }
    },
    parcelles: {
      handler(newParcelles) {
        if (this.mapLoaded && newParcelles.length > 0) {
          this.updateMapData();
        }
      },
      deep: true,
    },
  },

  beforeDestroy() {
    this.cleanupMap();
  },

  methods: {
    cleanupMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.mapLoaded = false;
    },

    initMap() {
      // Nettoyer la carte existante avant d'en créer une nouvelle
      this.cleanupMap();

      mapboxgl.accessToken = this.accessToken;

      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [0.824782, 8.619543], // Centre du Togo
        zoom: 6.4,
      });

      // Ajouter les contrôles
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
      this.map.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: true,
        }),
        'top-right'
      );

      this.map.on('load', () => {
        this.mapLoaded = true;
        this.updateMapData();
      });
    },

    zoomToParcelle(parcelleId) {
      if (!this.map || !parcelleId) {
        return;
      }
      const parcelle = this.parcellesAvecCoordonneesList.find(p => p.id === parcelleId);
      if (!parcelle) {
        return;
      }
      let gpsData;
      try {
        gpsData = typeof parcelle.gps === 'string' ? JSON.parse(parcelle.gps) : parcelle.gps;
      } catch (error) {
        return;
      }
      if (!Array.isArray(gpsData) || gpsData.length === 0) {
        return;
      }
      // Déterminer le centre selon le type de géométrie
      let center;
      if (gpsData.length === 2 && typeof gpsData[0] === 'number') {
        // Point : [lng, lat]
        center = gpsData;
      } else if (Array.isArray(gpsData) && gpsData.length > 0) {
        // Polygone : calculer le centre
        const coords = Array.isArray(gpsData[0]) ? gpsData[0] : gpsData; // Premier anneau du polygone
        center = coords; // Prendre le premier point comme centre
      } else {
        return;
      }
      // Zoomer sur la parcelle
      console.log('Zooming to parcelle:', center);
      this.map.flyTo({
        center: center,
        zoom: 20,
        essential: true,
      });
    },

    updateMapData() {
      if (!this.map || !this.mapLoaded) {
        return;
      }

      // Attendre que le style soit chargé avant de manipuler la carte
      if (!this.map.isStyleLoaded()) {
        this.map.on('style.load', () => {
          this.updateMapData();
        });
        return;
      }

      // Supprimer les sources et layers existants
      if (this.map.getSource('places')) {
        if (this.map.getLayer('places')) {
          this.map.removeLayer('places');
        }
        this.map.removeSource('places');
      }

      if (this.map.getSource('parcelles')) {
        if (this.map.getLayer('parcelles-fill')) {
          this.map.removeLayer('parcelles-fill');
        }
        if (this.map.getLayer('parcelles-outline')) {
          this.map.removeLayer('parcelles-outline');
        }
        this.map.removeSource('parcelles');
      }

      // Create GeoJSON from parcelles data
      const geojson = {
        type: 'FeatureCollection',
        features: this.parcelles.map(parcelle => {
          if (!parcelle.gps) {
            return null;
          }

          let gpsData;
          try {
            // Vérifier si c'est déjà un objet ou si c'est une chaîne à parser
            if (typeof parcelle.gps === 'string') {
              gpsData = JSON.parse(parcelle.gps);
            } else if (typeof parcelle.gps === 'object') {
              gpsData = parcelle.gps;
            } else {
              return null;
            }
          } catch (error) {
            console.error('Erreur parsing GPS pour parcelle', parcelle.id, ':', error);
            return null;
          }

          let coordinates;
          let geometryType;

          // Détecter si c'est un point unique ou un tableau de coordonnées
          const isPoint = Array.isArray(gpsData) &&
            gpsData.length === 2 &&
            typeof gpsData[0] === 'number' &&
            typeof gpsData[1] === 'number';

          if (isPoint) {
            coordinates = gpsData; // [lng, lat]
            geometryType = 'Point';
          } else if (Array.isArray(gpsData) && gpsData.length > 0) {
            // Polygon : [[lng, lat], [lng, lat], ...]
            const features = gpsData;
            const isClosedPolygon = features.length >= 3 &&
              features[0][0] === features[features.length - 1][0] &&
              features[0][1] === features[features.length - 1][1];

            if (!isClosedPolygon) {
              return null; // Ne traiter que les polygones fermés
            }

            geometryType = 'Polygon';
            coordinates = [features];
          } else {
            return null;
          }

          return {
            type: 'Feature',
            properties: {
              id: parcelle.id,
              code: parcelle.code || `#${parcelle.id}`,
              libelle: parcelle.libelle || '',
              superficie: parcelle.superficie || 0,
              nombre_sacs: parcelle.nombre_sacs || 0,
              poids_total: parcelle.poids_total || 0,
              producteur: parcelle.producteur || '',
            },
            geometry: {
              type: geometryType,
              coordinates: coordinates,
            },
          };
        }).filter(feature => feature !== null),
      };

      console.log('GeoJSON généré:', geojson);

      if (geojson.features.length === 0) {
        return;
      }

      // Séparer les points et les polygones
      const pointFeatures = geojson.features.filter(f => f.geometry.type === 'Point');
      const polygonFeatures = geojson.features.filter(f => f.geometry.type === 'Polygon');

      // Charger l'image du marqueur pour les points
      this.map.loadImage(customMarker, (error, image) => {
        if (error) {
          console.error('Erreur chargement image marqueur:', error);
        } else {
          if (!this.map.hasImage('parcelle-icon')) {
            this.map.addImage('parcelle-icon', image);
          }
        }

        // Ajouter les points avec marqueur personnalisé
        if (pointFeatures.length > 0) {
          this.map.addSource('places', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: pointFeatures,
            },
          });

          // Charger l'image du marqueur
          this.map.loadImage(customMarker, (error, image) => {
            if (error) {
              console.error('Erreur chargement image marqueur:', error);
            } else {
              if (!this.map.hasImage('parcelle-icon')) {
                this.map.addImage('parcelle-icon', image);
              }
            }

            this.map.addLayer({
              id: 'places',
              type: 'symbol',
              source: 'places',
              layout: {
                'icon-image': 'parcelle-icon',
                'icon-size': 0.8,
                'icon-allow-overlap': true,
              },
            });

            // Popup pour les points
            this.map.on('click', 'places', (e) => {
              const feature = e.features[0];
              const popupContent = this.createPopupContent(feature);
              new mapboxgl.Popup()
                .setLngLat(e.lngLat)
                .setHTML(popupContent)
                .addTo(this.map);
            });

            // Effet hover sur les points
            this.map.on('mouseenter', 'places', () => {
              this.map.getCanvas().style.cursor = 'pointer';
            });
            this.map.on('mouseleave', 'places', () => {
              this.map.getCanvas().style.cursor = '';
            });
          });
        }

        // Ajouter les polygones
        if (polygonFeatures.length > 0) {
          this.map.addSource('parcelles', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: polygonFeatures,
            },
          });

          // Couche de remplissage vert
          this.map.addLayer({
            id: 'parcelles-fill',
            type: 'fill',
            source: 'parcelles',
            paint: {
              'fill-color': '#00ab53',
              'fill-opacity': 0.3,
            },
          });

          // Couche de contour vert foncé
          this.map.addLayer({
            id: 'parcelles-outline',
            type: 'line',
            source: 'parcelles',
            paint: {
              'line-color': '#006633',
              'line-width': 2,
            },
          });

          // Popup pour les polygones
          this.map.on('click', 'parcelles-fill', (e) => {
            const feature = e.features[0];
            const popupContent = this.createPopupContent(feature);
            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(popupContent)
              .addTo(this.map);
          });

          // Effet hover sur les polygones
          this.map.on('mouseenter', 'parcelles-fill', () => {
            this.map.getCanvas().style.cursor = 'pointer';
          });
          this.map.on('mouseleave', 'parcelles-fill', () => {
            this.map.getCanvas().style.cursor = '';
          });
        }

        // Ajuster la vue pour voir toutes les parcelles
        this.fitMapToFeatures(geojson);
      });
    },

    createPopupContent(feature) {
      const props = feature.properties;
      return `
        <div style="padding: 8px; min-width: 200px;">
          <h4 style="margin: 0 0 8px 0; color: #333;">${props.code}</h4>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Producteur:</strong> ${props.producteur}</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Superficie:</strong> ${props.superficie} ha</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Nb sacs:</strong> ${props.nombre_sacs}</p>
          <p style="margin: 4px 0; font-size: 12px;"><strong>Poids:</strong> ${props.poids_total} T</p>
        </div>
      `;
    },

    fitMapToFeatures(geojson) {
      const bounds = new mapboxgl.LngLatBounds();
      let hasCoordinates = false;

      geojson.features.forEach(feature => {
        if (feature.geometry.type === 'Point') {
          bounds.extend(feature.geometry.coordinates);
          hasCoordinates = true;
        } else if (feature.geometry.type === 'Polygon') {
          feature.geometry.coordinates[0].forEach(coord => {
            bounds.extend(coord);
          });
          hasCoordinates = true;
        }
      });

      if (hasCoordinates) {
        this.map.fitBounds(bounds, {
          padding: 50,
        });
      }
    },

    handleClose() {
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style scoped>
.map-container {
  height: 500px;
  position: relative;
}

.map-stats {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
  justify-content: space-between;
}

.map {
  height: 100%;
  width: 100%;
  border-radius: 4px;
}

.dialog-footer {
  text-align: right;
}
</style>

